import { apiClient, ROUND_DELEGATION_HEADER } from '../utils/api-client';
import { throwForResponse } from './api-error';

/**
 * A team manager's or an athlete's helpers on one round: the phones they hand
 * some cards to, so that somebody with no account can record results for them
 * (RoundDelegation on the server, docs/ROUND_DELEGATION_PLAN.md).
 */

export type RoundDelegationKind = 'team' | 'athlete';

/** The sharer's list: still to take, working, stopped (the clock ran out), revoked. */
export type RoundDelegationStatus = 'pending' | 'active' | 'stopped' | 'revoked';

export interface DelegationParticipant {
  id: string;
  full_name: string;
  competition_category_id: string | null;
}

/** One helper, as the person who shared sees it. */
export interface RoundDelegation {
  id: string;
  kind: RoundDelegationKind;
  status: RoundDelegationStatus;
  helper_name: string | null;
  accepted_at: string | null;
  created_at: string;
  participants: DelegationParticipant[];
  /** The link's token: only in the answer that made it (create, reissue). */
  token?: string;
}

export interface RoundDelegationsPage {
  delegations: RoundDelegation[];
  delegableKinds: RoundDelegationKind[];
  /** Who a team manager may hand over here. */
  delegableParticipants: DelegationParticipant[];
}

/** The link's side: what somebody who scanned the code is shown. */
export type InvitationStatus = 'pending' | 'accepted' | 'revoked' | 'stopped';

export interface RoundDelegationInvitation {
  kind: RoundDelegationKind;
  status: InvitationStatus;
  /** A first name and an initial. */
  grantor_name: string;
  competition: { id: string; slug: string; name: string };
  round: { id: string; name: string; type: string };
}

/** The athlete whose card an athlete's helper writes: in the shape of a ScorableEntry. */
export interface DelegationGrantorEntry {
  participant_id: string;
  user_id: string | null;
  full_name: string;
  competition_category_id: string | null;
  competition_round_id: string | null;
  competition_round_name: string | null;
  confirmed: boolean;
  self: boolean;
}

/** The phone's own delegation, read with its header. */
export interface HelperDelegation extends Omit<RoundDelegationInvitation, 'status'> {
  status: RoundDelegationStatus;
  helper_name: string | null;
  can_write: boolean;
  participants: DelegationParticipant[];
  grantor_entry: DelegationGrantorEntry | null;
}

const base = (competitionSlug: string, roundId: string) =>
  `/api/competitions/${competitionSlug}/rounds/${roundId}/delegations`;

export const roundDelegationsService = {
  async list(competitionSlug: string, roundId: string): Promise<RoundDelegationsPage> {
    const response = await apiClient.get(base(competitionSlug, roundId), { showErrors: false });
    if (!response.ok) await throwForResponse(response, 'Failed to load helpers');

    const body = (await response.json()) as {
      data: RoundDelegation[];
      meta: { delegable_kinds: RoundDelegationKind[]; delegable_participants: DelegationParticipant[] };
    };
    return {
      delegations: body.data,
      delegableKinds: body.meta.delegable_kinds,
      delegableParticipants: body.meta.delegable_participants,
    };
  },

  async create(
    competitionSlug: string,
    roundId: string,
    kind: RoundDelegationKind,
    participantIds: string[] = []
  ): Promise<RoundDelegation> {
    const response = await apiClient.post(
      base(competitionSlug, roundId),
      { kind, participant_ids: participantIds },
      { showErrors: false }
    );
    if (!response.ok) await throwForResponse(response, 'Failed to share the round');

    return ((await response.json()) as { data: RoundDelegation }).data;
  },

  async updateParticipants(
    competitionSlug: string,
    roundId: string,
    delegationId: string,
    participantIds: string[]
  ): Promise<RoundDelegation> {
    const response = await apiClient.patch(
      `${base(competitionSlug, roundId)}/${delegationId}`,
      { participant_ids: participantIds },
      { showErrors: false }
    );
    if (!response.ok) await throwForResponse(response, 'Failed to change the athletes');

    return ((await response.json()) as { data: RoundDelegation }).data;
  },

  /** "Nuovo link": the old phone stops, the name and the athletes stay. */
  async reissue(competitionSlug: string, roundId: string, delegationId: string): Promise<RoundDelegation> {
    const response = await apiClient.post(
      `${base(competitionSlug, roundId)}/${delegationId}/reissue`,
      {},
      { showErrors: false }
    );
    if (!response.ok) await throwForResponse(response, 'Failed to make a new link');

    return ((await response.json()) as { data: RoundDelegation }).data;
  },

  async revoke(competitionSlug: string, roundId: string, delegationId: string): Promise<void> {
    const response = await apiClient.delete(`${base(competitionSlug, roundId)}/${delegationId}`, {
      showErrors: false,
    });
    if (!response.ok) await throwForResponse(response, 'Failed to revoke');
  },

  // --- The helper's side ------------------------------------------------------

  async invitation(token: string): Promise<RoundDelegationInvitation> {
    const response = await apiClient.get(`/api/round_delegations/${encodeURIComponent(token)}`, {
      showErrors: false,
    });
    if (!response.ok) await throwForResponse(response, 'Invitation not found');

    return ((await response.json()) as { data: RoundDelegationInvitation }).data;
  },

  /** Takes the link on this phone: the answer carries the phone's secret. */
  async accept(
    token: string,
    helperName: string
  ): Promise<RoundDelegationInvitation & { device_secret: string }> {
    const response = await apiClient.post(
      `/api/round_delegations/${encodeURIComponent(token)}/accept`,
      { helper_name: helperName },
      { showErrors: false }
    );
    if (!response.ok) await throwForResponse(response, 'Failed to accept');

    return ((await response.json()) as { data: RoundDelegationInvitation & { device_secret: string } }).data;
  },

  async current(credential: string): Promise<HelperDelegation> {
    const response = await apiClient.get('/api/round_delegation', {
      showErrors: false,
      headers: { [ROUND_DELEGATION_HEADER]: credential },
    });
    if (!response.ok) await throwForResponse(response, 'Delegation not found');

    return ((await response.json()) as { data: HelperDelegation }).data;
  },
};
